import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { StockyEntryService } from '../../services/stocky-entry.service';
import { ClearLot, SetLot } from '../../store/stock-entry.actions';

export interface Lot {
  id: string;
  numero: string;
  sacaNumero: string;
  quantityToCheck: number;
  quantityChecked: number;
  totalQuantity: number;
  lotDate: string;
}

@Component({
  selector: 'app-lots',
  templateUrl: './lots.component.html',
  styleUrls: ['./lots.component.sass'],
})
export class LotsComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');

  displayedColumns = ['lot', 'saca', 'quantityToCheck', 'quantityChecked', 'lotDate', 'reading'];

  lots: Lot[] = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;

  private _destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store,
    private _stockyEntryService: StockyEntryService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadLots();

    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      filter(value => !value?.trim()),
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadLots();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  loadLots(): void {
    const filtro = this.searchControl.value?.trim();
    const params: { Filtro?: string; PageSize: number; Page: number } = {
      PageSize: this.pageSize,
      Page: this.currentPage,
    };

    if (filtro) {
      params.Filtro = filtro;
    }

    this._stockyEntryService.getLots(params).subscribe({
      next: (response) => {
        const items: any[] = response?.page ?? [];

        this.lots = items.map((item: any) => ({
          id: item.id,
          numero: item.numero,
          sacaNumero: item.sacaNumero,
          quantityToCheck: item.quantidadeItensAConferir,
          quantityChecked: item.quantidadeItensConferidos,
          totalQuantity: item.totalItens,
          lotDate: new Date(item.dataLote).toLocaleDateString('pt-BR'),
        }));

        this.totalItems = response?.count ?? this.lots.length;
      },
      error: (err) => {
        const msg = err?.errors[0] || 'Erro ao buscar lotes.';
        this.toastr.error(msg);
      },
    });
  }

  onlyNumbers(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    this.searchControl.setValue(input.value, { emitEvent: false });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadLots();
  }

  handlePageEvent(e: PageEvent): void {
    this.pageSize = e.pageSize;
    this.currentPage = e.pageIndex + 1;
    this.loadLots();
  }

  generateNewLot(): void {
    this.store.dispatch(new ClearLot());
    this.router.navigate(['tvf/recebimento/criar']);
  }

  registerBag(): void {
    this.router.navigate(['tvf/recebimento/criar-saca']);
  }

  startReading(lot: Lot): void {
    this._stockyEntryService.getLots({ Filtro: String(lot.numero) }).subscribe({
      next: (response) => {
        const item = response?.page?.[0];
        if (!item) return;
        this.store.dispatch(new SetLot({
          id: item.id,
          numero: item.numero,
          sacaNumero: item.sacaNumero,
          status: item.status,
          quantityChecked: item.quantidadeItensConferidos,
          quantityToCheck: item.quantidadeItensAConferir,
          totalQuantity: item.totalItens,
          lotDate: new Date(item.dataLote).toLocaleDateString('pt-BR'),
        }));
        this.router.navigate(['tvf/recebimento/criar']);
      },
    });
  }
}
